import { fetchProductPrices, findVariantPrice } from './priceController.js';
import { PRODUCT_URLS, SIZE_DATA, COLORS_DATA, ACCESSORIES_DATA, INSTALLATION_PRICE, ANCHORING_SERVICE_PRICE, state } from '../settings.js';
import { pergola } from '../3d-configurator.js';

//  URL helpers 

function getModelUrl(sizeCode, colorBody) {
  const groupTitle = colorBody === '#000000' ? 'black-models' : 'aluminum-models';
  const group = PRODUCT_URLS.find(g => g.title === groupTitle);
  return group?.links.find(l => l.name === sizeCode)?.link ?? null;
}

function getAccessoryUrl(key) {
  const group = PRODUCT_URLS.find(g => g.title === 'accessories');
  return group?.links.find(l => l.name === key)?.link ?? null;
}

//     State helpers ────────────────────────────────────────────────────────────

// 'aluminium' | 'noir' | 'ral'
function getColorVariant(colorBody) {
  if (colorBody === '#000000') return 'noir';
  const entry = COLORS_DATA.find(c => c.hex === colorBody);
  if (entry?.ral || entry?.custom) return 'ral';
  return 'aluminium';
}

// countryType
function getCountryLabel(countryType) {
  return countryType === 0 ? 'Belgique' : 'France';
}

// Installation (in Belgium + has selected installation)
function wantsMontage() {
  return state.countryType === 0 && state.installation === true;
}

// Hardcoded fallbacks ──────────────────────────────────────────────────────

function hardcodedModelPrice() {
  const sizes = SIZE_DATA[state.shapeType];
  const s = sizes?.[state.sizeIndex] ?? sizes?.[0];
  return s?.price ?? 0;
}

function hardcodedAccessoryPrice(key) {
  const item = ACCESSORIES_DATA.flatMap(s => s.items).find(i => i.key === key);
  if (!item) return 0;
  const price = Array.isArray(item.price) ? item.price[0] : item.price;
  return price ?? 0;
}

// ****** Per-item resolvers ******

async function resolveModelPrice() {
  const sizes = SIZE_DATA[state.shapeType];
  const sizeEntry = sizes?.[state.sizeIndex] ?? sizes?.[0];
  if (!sizeEntry) return null;

  const url = getModelUrl(sizeEntry.code, state.colorBody);
  if (!url) return null;

  try {
    const variants = await fetchProductPrices(url);
    const match = findVariantPrice(variants, {
      country:      getCountryLabel(state.countryType),
      colorVariant: getColorVariant(state.colorBody),
      withMontage:  false, // model base price
    });
    return match?.price ?? null;
  } catch {
    return null;
  }
}

async function resolveAccessoryPrice(key) {
  const url = getAccessoryUrl(key);
  if (!url) return null;

  const withMontage = wantsMontage();

  try {
    const variants = await fetchProductPrices(url);

    // Standard path: multi-option accessories with country + color + montage in separate options
    let match = findVariantPrice(variants, {
      country:      getCountryLabel(state.countryType),
      colorVariant: getColorVariant(state.colorBody),
      withMontage,
    });

    // Fallback: accessories where color and/or montage are in option1/option2
    // (no country dimension — e.g. "Aluminium brut / Sans", "Thermolaqué noir / Avec montage")
    // Also handles single-option accessories ("Sans" / "Avec Montage") with no color axis.
    if (!match) {
      const colorVariant = getColorVariant(state.colorBody);
      match = variants.find(v => {
        const s = [v.country, v.colorType, v.montage].join(' ').toLowerCase();

        // If no color keyword exists anywhere in this variant, treat it as color-agnostic
        const hasColorKeyword = s.includes('noir') || s.includes('black') || s.includes('9005')
          || s.includes('couleur') || s.includes('choix') || s.includes('ral')
          || s.includes('aluminium') || s.includes('brut') || s.includes('thermolaqué');

        let matchColor;
        if (!hasColorKeyword) {
          matchColor = true; // no color axis on this product — matches any colorBody
        } else if (colorVariant === 'noir') {
          matchColor = s.includes('noir') || s.includes('black') || s.includes('9005');
        } else if (colorVariant === 'ral') {
          matchColor = s.includes('couleur') || s.includes('choix') || s.includes('ral');
        } else {
          // aluminium: exclude all non-aluminium keywords
          matchColor = !s.includes('noir') && !s.includes('black') && !s.includes('9005')
            && !s.includes('couleur') && !s.includes('choix') && !s.includes('thermolaqué');
        }

        const matchMontage = withMontage
          ? s.includes('montage') && !s.includes('sans')
          : s.includes('sans') || (v.country === '' && v.colorType === '' && v.montage === '');

        return matchColor && matchMontage;
      });
    }

    return match?.price ?? null;
  } catch {
    return null;
  }
}

// Window (sideWindow / backWindow): match by location, mode, and montage.
// option1 = "Sur le pignon" (back) | "Sur le côté" (side)
// option2 = "Manuel" | "Automatique"
// option3 = "Sans montage" | "Avec montage (+50€)"
async function resolveWindowPrice(key) {
  const url = getAccessoryUrl(key);
  if (!url) return null;

  const isAuto = (state.windowModes?.[key] ?? 'manual-window') === 'auto-window';
  const withInstall = wantsMontage();
  // Strip accents for robust matching of "côté" / "cote"
  const stripAccents = s => s.normalize('NFD').replace(/[̀-ͯ]/g, '');
  const locationKeyword = key === 'backWindow' ? 'pignon' : 'cote';

  try {
    const variants = await fetchProductPrices(url);
    const match = variants.find(v => {
      const opt1 = stripAccents(v.country).toLowerCase();
      const opt2 = v.colorType.toLowerCase();
      const opt3 = v.montage.toLowerCase();

      const matchLocation = opt1.includes(locationKeyword);

      const matchMode = isAuto
        ? opt2.includes('auto') || opt2.includes('automatique')
        : opt2.includes('manuel') || opt2.includes('manual');

      const matchMontage = withInstall
        ? opt3.includes('montage') && !opt3.includes('sans')
        : opt3.includes('sans') || opt3 === '';

      return matchLocation && matchMode && matchMontage;
    });
    return match?.price ?? null;
  } catch {
    return null;
  }
}

// Downpipes price
// (grey_plastic has no XML variant — falls back to hardcoded price)
async function resolveDownpipesPrice() {
  const url = getAccessoryUrl('downpipes');
  if (!url) return null;

  const mat = state.downpipeMaterial ?? 'grey_plastic';
  if (mat === 'grey_plastic') return null; // no XML variant- hardcoded fallback

  try {
    const variants = await fetchProductPrices(url);
    const match = variants.find(v => {
      const opt1 = v.country.toLowerCase(); // option1 =  material label
      if (mat === 'black_alum')     return opt1.includes('alu');
      if (mat === 'black_plastic')  return opt1.includes('pvc') || opt1.includes('plastic');
      return false;
    });
    return match?.price ?? null;
  } catch {
    return null;
  }
}

// Roof decor price depends on size + color + montage.
// option1 = "Xm?? - Code1 - Code2 - ..."  option2 = color  option3 = montage
async function resolveRoofDecorPrice() {
  const url = getAccessoryUrl('roofDecor');
  if (!url) return null;

  const sizes = SIZE_DATA[state.shapeType];
  const sizeEntry = sizes?.[state.sizeIndex] ?? sizes?.[0];
  if (!sizeEntry) return null;

  const sizeCode = sizeEntry.code; 
  const colorVariant = getColorVariant(state.colorBody);
  const montage = wantsMontage();

  try {
    const variants = await fetchProductPrices(url);
    const match = variants.find(v => {
      // option1 lists all codes for that length row — check if our code is in it
      if (!v.country.includes(sizeCode)) return false;

      const ct = v.colorType.toLowerCase();
      let matchColor;
      if (colorVariant === 'noir') {
        matchColor = ct.includes('noir') || ct.includes('black') || ct.includes('blackline');
      } else if (colorVariant === 'ral') {
        matchColor = ct.includes('couleur') || ct.includes('blackline');
      } else {
        matchColor = ct.includes('aluminium') || ct.includes('brut');
      }

      const mt = v.montage.toLowerCase();
      const matchMontage = montage
        ? mt.includes('montage') && !mt.includes('sans')
        : mt.includes('sans') || mt === '';

      return matchColor && matchMontage;
    });
    return match?.price ?? null;
  } catch {
    return null;
  }
}

// Veil = size of model
// option1 = "Couvrant N vitres - ..."  option2 = "Sans" | "Avec Montage (+50€)"
async function resolveVeilPrice() {
  const url = getAccessoryUrl('veil');
  if (!url) return null;

  const sizes = SIZE_DATA[state.shapeType];
  const sizeEntry = sizes?.[state.sizeIndex] ?? sizes?.[0];
  if (!sizeEntry) return null;

  //  "R306H" -> 06 -> 6.....
  const vitCount = parseInt(sizeEntry.code.slice(2, 4), 10);
  if (!vitCount) return null;

  try {
    const variants = await fetchProductPrices(url);
    const montage = wantsMontage();
    const match = variants.find(v => {
      const opt1 = v.country.toLowerCase();
      const opt2 = v.colorType.toLowerCase();
      const matchVit = opt1.includes(`${vitCount} vitres`);
      const matchMontage = montage
        ? opt2.includes('montage') && !opt2.includes('sans')
        : opt2.includes('sans') || opt2 === '';
      return matchVit && matchMontage;
    });
    return match?.price ?? null;
  } catch {
    return null;
  }
}


/**
 * Fetch live prices, update state.price,
 *  dispatch 'priceResolved' so the cart UI re-renders with the new total.
 */
export async function resolveAndUpdatePrice() {
  // 1. Model base price (URL switches to black-models when colorBody === '#000000')
  const modelPrice = (await resolveModelPrice()) ?? hardcodedModelPrice();
  state.resolvedModelPrice = modelPrice;
  let total = modelPrice;

  // 2. RAL color surcharge (flat, not from XML)
  const colorEntry = COLORS_DATA.find(c => c.hex === state.colorBody);
  if (colorEntry?.surcharge) total += colorEntry.surcharge;

  // 3. accessories (downpipes excluded from price)
  const placementCounts = {};
  const placed = pergola?.getPlacedAccessories?.() ?? [];
  placed.forEach(({ accessoryKey, permanent }) => {
    if (permanent) return;
    placementCounts[accessoryKey] = (placementCounts[accessoryKey] || 0) + 1;
  });

  state.resolvedAccessoryPrices = {};
  for (const key of state.subAccessoriesUrl) {
    if (key === 'downpipes') continue;
    let unitPrice;
    if (key === 'veil') {
      unitPrice = (await resolveVeilPrice()) ?? hardcodedAccessoryPrice(key);
    } else if (key === 'roofDecor') {
      unitPrice = (await resolveRoofDecorPrice()) ?? hardcodedAccessoryPrice(key);
    } else if (key === 'sideWindow' || key === 'backWindow') {
      unitPrice = (await resolveWindowPrice(key)) ?? hardcodedAccessoryPrice(key);
    } else {
      unitPrice = (await resolveAccessoryPrice(key)) ?? hardcodedAccessoryPrice(key);
    }
    state.resolvedAccessoryPrices[key] = unitPrice;
    total += unitPrice * (placementCounts[key] || 0);
  }

  // 4. Installation fee (Belgium)
  // Combinations:
  //   ground + no install (1&3): nothing
  //   ground + install   (1&4): +INSTALLATION_PRICE + ANCHORING_SERVICE_PRICE
  //   surface + no install(2&3): +0 (fixations free, cart item only)
  //   surface + install  (2&4): +INSTALLATION_PRICE (fixations free)
  if (state.countryType === 0) {
    const isSurface = state.anchorType === 'surface';
    if (state.installation) {
      total += INSTALLATION_PRICE;
      if (!isSurface) total += ANCHORING_SERVICE_PRICE;
    }
  }

  // 5. Persist on state and notify UI
  state.price = total;
  window.dispatchEvent(new CustomEvent('priceResolved', { detail: { total } }));
}

// Re-resolve price whenever the user switches manual/auto window mode
window.addEventListener('windowModeChanged', () => resolveAndUpdatePrice());
